import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import dayjs from "dayjs";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ScheduleEvent from "../SheduleEvent";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import {
  deleteProfessionalEvents,
  getProfessionalEvents,
} from "../../../../Redux/Reducers/calendarReducer";
import { useQuantityContext } from "../../../../Components/QuantityContext";
import { useUserDetailContext } from "../../../../Components/UserDetailContext";
import { useNavigate } from "react-router-dom";

const DeleteConfirmationDialog = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Delete Event"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you want to delete this event? This action cannot be
          undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained" autoFocus>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TimeSlotConflictDialog = ({ open, conflictInfo, onChangeTime, onSelectAnyway }) => {
  return (
    <Dialog
      open={open}
      onClose={onChangeTime}
      aria-labelledby="conflict-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          maxWidth: 380,
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogTitle
        id="conflict-dialog-title"
        sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 0 }}
      >
        Time Slot Unavailable
        <IconButton
          aria-label="close"
          onClick={onChangeTime}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <DialogContentText sx={{ color: "text.primary", fontSize: "0.95rem" }}>
          The time slot from{" "}
          <strong>{conflictInfo?.startTime}</strong> to{" "}
          <strong>{conflictInfo?.endTime}</strong> is already booked. Are you
          sure you want to select this time?
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onChangeTime}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#ccc",
            color: "text.primary",
            borderRadius: 2,
            flex: 1,
            "&:hover": { borderColor: "#999", backgroundColor: "#f5f5f5" },
          }}
        >
          Change Time
        </Button>
        <Button
          onClick={onSelectAnyway}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#0044C6",
            borderRadius: 2,
            flex: 1,
            "&:hover": { backgroundColor: "#0035a0" },
          }}
        >
          Select Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExpandableText = ({ text, maxLines = 3 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseInt(getComputedStyle(textRef.current).lineHeight);
      const maxHeight = lineHeight * maxLines;
      setNeedsTruncation(textRef.current.scrollHeight > maxHeight);
    }
  }, [text, maxLines]);

  if (!text) return null;

  return (
    <Box>
      <Typography
        ref={textRef}
        variant="body1"
        sx={{
          whiteSpace: "pre-wrap",
          lineHeight: 1.5,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: isExpanded ? "none" : maxLines,
          WebkitBoxOrient: "vertical",
          lineClamp: isExpanded ? "none" : maxLines,
        }}
      >
        {text}
      </Typography>
      {needsTruncation && (
        <Button
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            mt: 1,
            textTransform: "none",
            minWidth: "auto",
            padding: "2px 8px",
            fontSize: "0.75rem",
          }}
          color="primary"
        >
          {isExpanded ? "Show Less" : "Show More"}
        </Button>
      )}
    </Box>
  );
};

const ProfessionalCalender = ({ initialView = "month" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openSnackbar } = useQuantityContext();
  const { userDetails } = useUserDetailContext();
  const companyCalender =
    useSelector((state) => state?.calender?.professionalEventsData) || [];
  const ProfessionalData = useSelector(
    (state) => state?.ProfileArray?.professionaProfile
  );
  const [date, setDate] = useState(dayjs());
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dayDrawerOpen, setDayDrawerOpen] = useState(false);
  const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [pendingSubmitCallback, setPendingSubmitCallback] = useState(null);
  const ProfessionalExperience =
    useSelector((state) => state?.ProfileArray?.experienceData?.data) || [];
  const ProfessionalEducation =
    useSelector((state) => state?.ProfileArray?.educationData?.data) || [];
  const currentExperiences =
    ProfessionalExperience?.filter(
      (exp) => exp.is_currently_working === true && exp.is_verified
    ) || [];

  const currentEducation =
    ProfessionalEducation?.filter(
      (exp) => exp.is_pursuing === true && exp.is_verified
    ) || [];

  const jobAnalytics = useSelector((state) => state?.jobs?.jobAnalytics) ?? {
    data: [],
    jobApplicationsStatuswiseCount: {},
    jobOpenCloseCount: {},
  };

  const analyticsCompanies = jobAnalytics?.companiesPostedByUser || [];

  const matchedCompanies = analyticsCompanies?.length
    ? analyticsCompanies?.filter((jobCompany) => {
      return ProfessionalExperience?.some(
        (exp) =>
          exp?.company_name?.toLowerCase() ===
          jobCompany?.company_name?.toLowerCase() &&
          exp?.is_currently_working === true &&
          exp?.is_verified === true
      );
    })
    : ProfessionalExperience?.filter(
      (exp) => exp?.is_currently_working === true && exp?.is_verified === true
    ) || [];

  useEffect(() => {
    dispatch(getProfessionalEvents());
  }, [dispatch]);

  // Check if current user is the creator of the event
  const isEventCreator = (event) => {
    return (
      event?.createdBy?.toLowerCase() === userDetails?.email?.toLowerCase()
    );
  };

  /**
   * Returns true if the event has NOT yet started (start time is in the future).
   * Edit and Delete actions are only allowed for upcoming events.
   */
  const isEventEditable = (event) => {
    if (!event?.startTime) return false;
    return dayjs(event.startTime).isAfter(dayjs());
  };

  // Check if a new event overlaps with any existing events
  const checkTimeConflict = useCallback(
    (newStartTime, newEndTime, onConfirm, excludeEventId = null) => {
      const newStart = dayjs(newStartTime);
      const newEnd = dayjs(newEndTime);

      const conflictingEvent = companyCalender.find((event) => {
        if (excludeEventId && event.id === excludeEventId) return false;
        const existingStart = dayjs(event.startTime);
        const existingEnd = dayjs(event.endTime);
        // Overlap: new start < existing end AND new end > existing start
        return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
      });

      if (conflictingEvent) {
        setConflictInfo({
          startTime: dayjs(conflictingEvent.startTime).format("HH:mm:ss"),
          endTime: dayjs(conflictingEvent.endTime).format("HH:mm:ss"),
        });
        setPendingSubmitCallback(() => onConfirm);
        setConflictDialogOpen(true);
        return true; // conflict found
      }
      return false; // no conflict
    },
    [companyCalender]
  );

  const handleConflictChangeTime = () => {
    setConflictDialogOpen(false);
    setConflictInfo(null);
    setPendingSubmitCallback(null);
    setScheduleDrawerOpen(true);
  };

  const handleConflictSelectAnyway = () => {
    setConflictDialogOpen(false);
    setConflictInfo(null);
    if (pendingSubmitCallback) {
      pendingSubmitCallback();
    }
    setPendingSubmitCallback(null);
  };

  const isPastDate = (dateToCheck) => {
    const today = dayjs().startOf("day");
    return dateToCheck.isBefore(today, "day");
  };

  // Check if a date-time is in the past (including today but before current time)
  const isPastDateTime = (dateTimeToCheck) => {
    const now = dayjs();
    return dateTimeToCheck.isBefore(now);
  };

  const handleDateClick = (newValue) => {
    setDate(newValue);
    const selectedDayStr = newValue.format("YYYY-MM-DD");

    const dayEvents = companyCalender?.filter(
      (event) => dayjs(event.startTime).format("YYYY-MM-DD") === selectedDayStr
    );

    setSelectedDateEvents(dayEvents);
    setActiveModal("day");
    setEventDrawerOpen(false);
    setDayDrawerOpen(true);
  };

  const handleAddNewEvent = useCallback(() => {
    const hasWorkExperience = currentExperiences && currentExperiences.length > 0;
    const hasEducation = currentEducation && currentEducation.length > 0;

    if (!hasWorkExperience && !hasEducation) {
      openSnackbar(
        "You need at least one verified working company or current education to schedule professional events. Please verify your profile to schedule events.",
        "warning"
      );
      navigate("/myprofile?mainTab=professional_profile");
      return;
    }

    setEditMode(false);
    setSelectedEvent(null);
    setActiveModal("schedule");
    setDayDrawerOpen(false);
    setEventDrawerOpen(false);
    setTimeout(() => {
      setScheduleDrawerOpen(true);
    }, 10);
  }, [currentExperiences, currentEducation]);

  const handleEditEvent = (event) => {
    // Guard: prevent editing if event has already started
    if (!isEventEditable(event)) return;

    setEditMode(true);
    setSelectedEvent(event);
    setActiveModal("schedule");
    setEventDrawerOpen(false);
    setDayDrawerOpen(false);
    setTimeout(() => {
      setScheduleDrawerOpen(true);
    }, 10);
  };

  const handleDeleteEvent = (eventId, event) => {
    // Guard: prevent deleting if event has already started
    if (event && !isEventEditable(event)) return;

    setSelectedEventId(eventId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteProfessionalEvents(selectedEventId));
      setDeleteDialogOpen(false);
      setDayDrawerOpen(false);
      setSelectedDateEvents([]);
      setEventDrawerOpen(false);
      dispatch(getProfessionalEvents());
    } catch (error) {
      console.error("Failed to delete event:", error);
    }
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case "Interview":
        return "#e3f2fd";
      case "Others":
        return "#e0f7fa";
      default:
        return "#f5f5f5";
    }
  };

  const handleLocationClick = (location) => {
    if (location) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        location
      )}`;
      window.open(mapsUrl, "_blank");
    }
  };

  const handleMeetLinkClick = (meetLink) => {
    if (meetLink) {
      window.open(meetLink, "_blank");
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventDrawerOpen(true);
  };

  const handleCloseEventDrawer = () => {
    setEventDrawerOpen(false);
    setSelectedEvent(null);
  };

  const DayViewTimeGrid = ({ events, date }) => {
    const getTimeSlots = () => {
      const slots = [];
      for (let i = 0; i < 24; i++) {
        slots.push(dayjs().hour(i).minute(0));
      }
      return slots;
    };

    const timeSlots = getTimeSlots();

    return (
      <Box sx={{ height: "100%", overflow: "auto", backgroundColor: "#fff" }}>
        {/* Today header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <Button variant="outlined" startIcon={<TodayRoundedIcon />}>
            {date.format("MMMM D, YYYY")}
          </Button>
          {/* Show add button only for today and future dates */}
          {!isPastDate(date) && (
            <IconButton
              onClick={() => {
                handleCloseEventDrawer();
                handleAddNewEvent();
              }}
            >
              <AddCircleOutlineRoundedIcon color="primary" />
            </IconButton>
          )}
        </Box>

        {/* Time slots grid */}
        {timeSlots?.map((slot) => {
          const slotEvents =
            events?.filter(
              (event) => dayjs(event?.startTime).hour() === slot.hour()
            ) || [];

          return (
            <Box
              key={slot.format("HH:mm")}
              sx={{
                display: "flex",
                borderBottom: "1px solid #eee",
                minHeight: "60px",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: "60px",
                  p: 1,
                  color: "text.secondary",
                  fontSize: "12px",
                  textAlign: "right",
                }}
              >
                {slot.format("h A")}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  position: "relative",
                  borderLeft: "1px solid #eee",
                }}
              >
                {slotEvents?.map((event, index) => {
                  const startTime = dayjs(event.startTime);
                  const endTime = dayjs(event.endTime);

                  const startMinutes =
                    startTime.hour() * 60 + startTime.minute();
                  const endMinutes = endTime.hour() * 60 + endTime.minute();
                  const duration = endMinutes - startMinutes;

                  const topPosition = (startMinutes / 1440) * 100;
                  const height = (duration / 1440) * 100;

                  return (
                    <Card
                      key={`${event.id}-${index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                      }}
                      sx={{
                        position: "absolute",
                        left: "2px",
                        right: "2px",
                        top: `${topPosition}%`,
                        height: `${height}%`,
                        minHeight: "48px",
                        p: 0.5,
                        backgroundColor: getEventColor(event.eventType),
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: 2,
                          zIndex: 2,
                          transform: "scale(1.02)",
                          transition: "all 0.2s ease",
                        },
                        zIndex: 1,
                        overflow: "hidden",
                        border:
                          event.eventType === "Interview"
                            ? "1px solid #90caf9"
                            : "1px solid #80deea",
                      }}
                    >
                      <Box sx={{ width: "100%", height: "100%" }}>
                        <Typography
                          variant="caption"
                          display="block"
                          noWrap
                          sx={{ fontSize: "10px", fontWeight: "bold" }}
                        >
                          {event.title}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "8px" }}
                          >
                            {startTime.format("h:mm A")} -{" "}
                            {endTime.format("h:mm A")}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            sx={{ fontSize: "8px" }}
                            noWrap
                          >
                            {event?.eventType}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleCloseDayDrawer = () => {
    setDayDrawerOpen(false);
    setSelectedDateEvents([]);
    setEventDrawerOpen(false);
    dispatch(getProfessionalEvents());
  };

  const getUniqueEvents = (events) => {
    const map = new Map();
    events.forEach((event) => {
      map.set(event.id, event);
    });
    return Array.from(map.values());
  };

  const todaysEvents = getUniqueEvents(
    companyCalender?.filter(
      (event) =>
        dayjs(event?.startTime).format("YYYY-MM-DD") ===
        dayjs().format("YYYY-MM-DD")
    )
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "25vw",
        maxWidth: "400px",
        margin: "0 auto",
      }}
    >
      {/* Monthly View */}
      <Card
        sx={{
          p: 1,
          overflow: "hidden",
          width: "100%",
          margin: "auto",
          flexShrink: 0,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={date}
            onChange={handleDateClick}
            onMonthChange={(newValue) => {
              setDate(newValue);
            }}
            sx={{
              width: "100%",
              "& .MuiDateCalendar-root": { width: "100%" },
              "& .MuiPickersCalendarHeader-root": {
                paddingLeft: "8px",
                paddingRight: "8px",
              },
              "& .MuiDayCalendar-header": { width: "100%" },
              "& .MuiDayCalendar-weekContainer": { width: "100%" },
              "& .MuiPickersDay-day": {
                width: "32px",
                height: "32px",
                margin: "2px",
              },
            }}
            slots={{
              day: (props) => {
                const dayStr = props?.day?.format("YYYY-MM-DD");
                const dayEvents = getUniqueEvents(
                  companyCalender?.filter(
                    (event) =>
                      dayjs(event?.startTime).format("YYYY-MM-DD") === dayStr
                  )
                );

                return (
                  <PickersDay
                    {...props}
                    sx={{
                      width: "32px",
                      height: "32px",
                      fontSize: "0.875rem",
                      ...(dayEvents?.length > 0 && {
                        backgroundColor: getEventColor(dayEvents[0]?.eventType),
                        color: "#1976d2",
                        "&:hover": {
                          backgroundColor:
                            dayEvents[0]?.eventType === "Interview"
                              ? "#bbdefb"
                              : "#b2ebf2",
                        },
                      }),
                    }}
                  />
                );
              },
            }}
          />
        </LocalizationProvider>
      </Card>

      {/* Today's Events Section */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          maxHeight: "50vh",
          width: "100%",
        }}
      >
        <Typography variant="h6" sx={{ p: 1 }}>
          Today's Events
        </Typography>

        {todaysEvents?.length === 0 ? (
          <Typography variant="body1" color="text.secondary" align="center">
            No events scheduled for today
          </Typography>
        ) : (
          todaysEvents
            ?.sort((a, b) => dayjs(a?.startTime).diff(dayjs(b?.startTime)))
            ?.map((event) => (
              <Accordion
                key={event?.id}
                sx={{
                  mb: 1,
                  "&:before": { display: "none" },
                  backgroundColor: getEventColor(event?.eventType),
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor:
                      event?.eventType === "Interview" ? "#bbdefb" : "#b2ebf2",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": { margin: "8px 0" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {event?.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {dayjs(event?.startTime).format("h:mm A")} -{" "}
                        {dayjs(event?.endTime).format("h:mm A")}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {(event?.interviewType === "F2F" ||
                        (event?.eventType === "Others" && event?.location)) &&
                        event?.location && (
                          <LocationOnRoundedIcon
                            fontSize="small"
                            color="primary"
                          />
                        )}
                      {event?.interviewType === "Remote" && event?.meetLink && (
                        <VideocamRoundedIcon fontSize="small" color="primary" />
                      )}
                      {!event?.interviewType && event?.meet_link && (
                        <VideocamRoundedIcon fontSize="small" color="primary" />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {event?.eventType}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {event?.description && (
                      <ExpandableText
                        text={event?.description}
                        maxLength={100}
                      />
                    )}

                    {(event?.interviewType === "F2F" ||
                      (event?.eventType === "Others" && event?.location)) &&
                      event?.location && (
                        <Button
                          variant="text"
                          onClick={() => handleLocationClick(event?.location)}
                          startIcon={<LocationOnRoundedIcon />}
                          sx={{
                            alignSelf: "flex-start",
                            textTransform: "none",
                          }}
                        >
                          {event?.location}
                        </Button>
                      )}

                    {event?.interviewType === "Remote" && event?.meetLink && (
                      <Button
                        variant="text"
                        onClick={() => handleMeetLinkClick(event?.meetLink)}
                        startIcon={<VideocamRoundedIcon />}
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                      >
                        Join Meeting
                      </Button>
                    )}

                    {!event?.interviewType && event?.meet_link && (
                      <Button
                        variant="text"
                        onClick={() => handleMeetLinkClick(event?.meet_link)}
                        startIcon={<VideocamRoundedIcon />}
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                      >
                        Join Meeting
                      </Button>
                    )}

                    {event?.attendees?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Attendees:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {event?.attendees?.map((attendee, index) => (
                            <Chip
                              key={index}
                              avatar={
                                <Avatar
                                  src={
                                    attendee?.profile_pic ||
                                    "https://www.shutterstock.com/image-vector/default-avatar-profile-icon-transparent-600nw-2463868853.jpg"
                                  }
                                  alt={attendee?.name}
                                />
                              }
                              label={attendee?.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Edit/Delete buttons: only shown to creator AND only if event hasn't started yet */}
                    {isEventCreator(event) && isEventEditable(event) && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                          mt: 1,
                        }}
                      >
                        <Button
                          size="small"
                          startIcon={<EditRoundedIcon />}
                          onClick={() => handleEditEvent(event)}
                          color="primary"
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteRoundedIcon />}
                          onClick={() => handleDeleteEvent(event.id, event)}
                          color="error"
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
        )}
      </Box>

      {/* Day View Drawer */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: dayDrawerOpen ? 0 : "-25vw",
          width: "25vw",
          height: "100%",
          backgroundColor: "#fff",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          transition: "right 0.3s ease",
          zIndex: 1900,
          pointerEvents: scheduleDrawerOpen ? "none" : "auto",
        }}
      >
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <IconButton onClick={handleCloseDayDrawer}>
                <ArrowBackIosRoundedIcon />
              </IconButton>
              <Typography variant="h6">
                {date.format("MMMM D, YYYY")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <DayViewTimeGrid events={selectedDateEvents} date={date} />
          </Box>
        </Box>
      </Box>

      {/* Event Details Drawer */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: eventDrawerOpen ? 0 : "-400px",
          width: "400px",
          height: "100%",
          backgroundColor: "#fff",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          transition: "right 0.3s ease",
          zIndex: 2100,
          overflow: "auto",
          pointerEvents: scheduleDrawerOpen ? "none" : "auto",
        }}
      >
        <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
          {selectedEvent && (
            <>
              {/* Header */}
              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pb: 2,
                  borderBottom: "1px solid #eee",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <img
                    src={
                      selectedEvent?.companyDetails?.logo ||
                      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR7NnXia5DWq6qfBisS5mDI7r8xa5sT8cuvnA&s"
                    }
                    alt={`${selectedEvent?.companyDetails?.companyName} logo`}
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: "contain",
                      borderRadius: "4px",
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#1a1a1a",
                      textTransform: "capitalize",
                    }}
                  >
                    {selectedEvent?.companyDetails?.companyName ||
                      "Unknown Company"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {/* Edit/Delete icon buttons: only shown to creator AND only if event hasn't started yet */}
                  {isEventCreator(selectedEvent) &&
                    isEventEditable(selectedEvent) && (
                      <>
                        <IconButton
                          onClick={() => handleEditEvent(selectedEvent)}
                          color="primary"
                          size="small"
                        >
                          <EditRoundedIcon />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            handleDeleteEvent(selectedEvent?.id, selectedEvent)
                          }
                          color="primary"
                          size="small"
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      </>
                    )}
                  <IconButton onClick={handleCloseEventDrawer}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Title
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {selectedEvent?.title}
                  </Typography>
                </Box>
              </Box>

              {/* Description */}
              {selectedEvent?.description && (
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: "#f8f9fa",
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  <ExpandableText
                    text={selectedEvent?.description}
                    maxLength={20}
                  />
                </Box>
              )}

              {/* Time & Duration */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Date & Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dayjs(selectedEvent?.startTime).format(
                        "DD MMM YYYY, hh:mm A"
                      )}{" "}
                      - {dayjs(selectedEvent?.endTime).format("hh:mm A")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Interview Type for Interview events */}
              {selectedEvent?.meetingType === "Interview" &&
                selectedEvent?.interviewType && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Interview Type
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                      <Typography variant="body1">
                        {selectedEvent?.interviewType}
                      </Typography>
                    </Paper>
                  </Box>
                )}

              {/* Location for F2F Interviews or Other events with location */}
              {(selectedEvent?.meetingType === "F2F" ||
                (selectedEvent?.eventType === "Others" &&
                  selectedEvent?.location)) &&
                selectedEvent?.location && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Location
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                      <Button
                        variant="text"
                        onClick={() =>
                          handleLocationClick(selectedEvent?.location)
                        }
                        startIcon={<LocationOnRoundedIcon />}
                        sx={{ textTransform: "none" }}
                      >
                        {selectedEvent?.location}
                      </Button>
                    </Paper>
                  </Box>
                )}

              {/* Meet Link for Remote Interviews */}
              {selectedEvent?.meetingType === "Remote" &&
                selectedEvent?.meetLink && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Meeting Link
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                      <Button
                        variant="text"
                        onClick={() =>
                          handleMeetLinkClick(selectedEvent?.meetLink)
                        }
                        startIcon={<VideocamRoundedIcon />}
                        sx={{ textTransform: "none" }}
                      >
                        Join Virtual Meeting
                      </Button>
                    </Paper>
                  </Box>
                )}

              {selectedEvent?.interviewType === "Remote" &&
                selectedEvent?.meetLink && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Meeting Link
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                      <Button
                        variant="text"
                        onClick={() =>
                          handleMeetLinkClick(selectedEvent?.meetLink)
                        }
                        startIcon={<VideocamRoundedIcon />}
                        sx={{ textTransform: "none" }}
                      >
                        Join Virtual Meeting
                      </Button>
                    </Paper>
                  </Box>
                )}

              {/* Fallback for meet_link field */}
              {!selectedEvent?.interviewType && selectedEvent?.meet_link && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Meeting Link
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                    <Button
                      variant="text"
                      onClick={() =>
                        handleMeetLinkClick(selectedEvent?.meet_link)
                      }
                      startIcon={<VideocamRoundedIcon />}
                      sx={{ textTransform: "none" }}
                    >
                      Join Virtual Meeting
                    </Button>
                  </Paper>
                </Box>
              )}

              {/* Attendees */}
              {selectedEvent?.attendees &&
                selectedEvent?.attendees?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Attendees
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedEvent?.attendees?.map((attendee, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1,
                            backgroundColor: "#f8f9fa",
                            borderRadius: 2,
                          }}
                        >
                          <Avatar
                            src={
                              attendee?.profile_pic ||
                              "https://www.shutterstock.com/image-vector/default-avatar-profile-icon-transparent-600nw-2463868853.jpg"
                            }
                            sx={{ width: 32, height: 32, mr: 1 }}
                          />
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {attendee?.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {attendee?.email}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

              {/* Created By Information */}
              {selectedEvent?.createdBy && (
                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #eee" }}>
                  <Typography variant="body2" color="text.secondary">
                    Created by: {selectedEvent?.createdBy}
                  </Typography>
                  {isEventCreator(selectedEvent) && (
                    <Typography variant="caption" color="primary">
                      (You created this event)
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Backdrop for event drawer */}
      {eventDrawerOpen && !scheduleDrawerOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 2099,
          }}
          onClick={handleCloseEventDrawer}
        />
      )}

      {/* Backdrop for day drawer */}
      {dayDrawerOpen && !scheduleDrawerOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1899,
          }}
          onClick={handleCloseDayDrawer}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {/* Time Slot Conflict Dialog */}
      <TimeSlotConflictDialog
        open={conflictDialogOpen}
        conflictInfo={conflictInfo}
        onChangeTime={handleConflictChangeTime}
        onSelectAnyway={handleConflictSelectAnyway}
      />

      {/* Schedule Event Drawer */}
      <ScheduleEvent
        open={scheduleDrawerOpen}
        onClose={() => {
          setScheduleDrawerOpen(false);
          setEditMode(false);
          setSelectedEvent(null);
          dispatch(getProfessionalEvents());
        }}
        selectedEvent={editMode ? selectedEvent : null}
        selectedDate={date}
        isPastDate={isPastDate(date)}
        isPastDateTime={isPastDateTime}
        onCheckTimeConflict={checkTimeConflict}
      />
    </Box>
  );
};

export default ProfessionalCalender;